import { useEffect, useState } from 'react'
import { api, ApiError } from '../api/client'
import type { CaseMovement, ResearchCase, ResearchQuery, ResearchSummary,
              UpcomingCatalyst } from '../api/client'

/** Was die Fälle-Seite braucht — aus vier Listen und zwei Zählungen. */
export interface FaelleDaten {
  faelle: ResearchCase[]
  gesamt: number | null
  bewegungen: CaseMovement[]
  termine: UpcomingCatalyst[]
  zahlen: ResearchSummary | null
  /** Fälle, die sich in den letzten sieben Tagen gerührt haben. */
  bewegt7: number | null
}

/** Wie weit die Bewegungen zurückreichen, in Tagen. */
const BEWEGUNGEN_TAGE = 30
/** Wie weit die Termine vorausschauen, in Tagen. */
const TERMINE_TAGE = 30

/**
 * Holt Fälle, Bewegungen, Termine und Kennzahlen.
 *
 * DIE FÄLLE SIND DIE WAHRHEIT: Filter laufen über die Fallliste am Server;
 * Bewegungen und Termine werden anschließend auf diese Fälle eingeschränkt.
 * So gilt ein Filter in beiden Sichten gleich, ohne dass jede Nebenliste
 * jeden Filter kennen müsste.
 */
export function useFaelle(params: ResearchQuery, enabled: boolean): {
  daten: FaelleDaten | null
  laedt: boolean
  fehler: string | null
  denied: boolean
} {
  const [daten, setDaten] = useState<FaelleDaten | null>(null)
  const [laedt, setLaedt] = useState(enabled)
  const [fehler, setFehler] = useState<string | null>(null)
  const [denied, setDenied] = useState(false)
  const schluessel = JSON.stringify(params)

  useEffect(() => {
    if (!enabled) return
    const controller = new AbortController()
    const { signal } = controller
    setLaedt(true)
    const jetzt = new Date()
    const seit = new Date(jetzt.getTime() - BEWEGUNGEN_TAGE * 86_400_000).toISOString()
    const woche = new Date(jetzt.getTime() - 7 * 86_400_000).toISOString()
    const weich = <T,>(p: Promise<T>): Promise<T | null> => p.catch(() => null)

    api.researchCases({ ...params, size: 200, total: true }, signal).then(async (seite) => {
      const [bewegungen, termine, zahlen, bewegt] = await Promise.all([
        weich(api.researchUpdates({ since: seit, limit: 100 }, signal)),
        weich(api.researchCatalysts({ horizon: TERMINE_TAGE, limit: 50 }, signal)),
        weich(api.researchSummary(signal)),
        weich(api.researchCases({ changed_since: woche, size: 1, total: true }, signal)),
      ])
      if (signal.aborted) return
      setDaten({
        faelle: seite.items, gesamt: seite.total,
        bewegungen: bewegungen ?? [], termine: termine ?? [], zahlen,
        bewegt7: bewegt?.total ?? null,
      })
      setFehler(null)
      setDenied(false)
    }).catch((e: unknown) => {
      if (signal.aborted) return
      if (e instanceof ApiError && e.status === 403) { setDenied(true); return }
      setFehler(e instanceof Error ? e.message : String(e))
    }).finally(() => { if (!signal.aborted) setLaedt(false) })

    return () => controller.abort()
    // `schluessel` fasst die Parameter zusammen; das Objekt selbst wäre bei
    // jedem Rendern neu.
  }, [schluessel, enabled]) // eslint-disable-line react-hooks/exhaustive-deps

  return { daten, laedt, fehler, denied }
}
